import BaseService from "./BaseService";
import {
  ProtocolRequest,
  ProtocolResponse,
  ProtocolsResponse,
  PaginatedProtocolsResponse
} from "./mappers/ProtocolMapper";
import { UsersResponse } from "./mappers/UserMapper";
import store from "@/store";

class ProtocolService extends BaseService {
  constructor() {
    super();
  }

  createProtocol(data) {
    store.dispatch("app/setPendingChanges", false);
    return this.post(`protocols/`, ProtocolRequest(data))
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  createCompanyProtocol(id, data) {
    store.dispatch("app/setPendingChanges", false);
    return this.post(`companies/${id}/protocols/`, ProtocolRequest(data))
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  generateExternalUrl(id) {
    return this.post(`/protocols/${id}/generateexternalurl/`)
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  clearExternalUrl(id) {
    return this.post(`/protocols/${id}/clearexternalurl/`)
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  sendEmailToResponsibleAndReporter(id) {
    return this.post(`/protocols/${id}/sendemailtoresponsibleandreporter/`)
      .then((response) => response.data)
      .then((data) => UsersResponse(data));
  }

  getProtocol(id) {
    return this.get(`protocols/${id}/`)
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  getProtocols(field1 = "") {
    return this.get(`protocols/?field1=${field1}`)
      .then((response) => response.data)
      .then((data) => ProtocolsResponse(data));
  }

  getMyActiveReadReceiptProtocols(
    field1 = "",
    readReceiptActive = true,
    search = "",
    viewed = "",
    signed = "",
    page = 1,
    pageSize = 5
  ) {
    return this.get(
      `my/protocols/0/?readreceipt_active=${readReceiptActive}&field1=${field1}&search=${search}&viewed=${viewed}&signed=${signed}&page=${page}&page_size=${pageSize}`
    ).then((response) => PaginatedProtocolsResponse(response.data));
  }

  updateProtocol(id, data) {
    store.dispatch("app/setPendingChanges", false);
    return this.patch(`protocols/${id}/`, ProtocolRequest(data))
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  deleteProtocol(id) {
    store.dispatch("app/setPendingChanges", false);
    return this.delete(`protocols/${id}/`);
  }

  getCompanyProtocolsByType(id, type, page = 1, pageSize = 5, search = "") {
    return this.get(
      `companies/${id}/protocols/?questionnaire__questionnaire_type=${type}&page=${page}&page_size=${pageSize}&search=${search}`
    ).then((response) => PaginatedProtocolsResponse(response.data));
  }

  getCompanyProtocols(id, field1 = "", page = 1, pageSize = 5, parent = "", parent_info = "", search = "", ids = "") {
    return this.get(
      `companies/${id}/protocols/?id=${ids}&field1=${field1}&page=${page}&page_size=${pageSize}&parent=${parent}&parent_info=${parent_info}&search=${search}`
    ).then((response) => PaginatedProtocolsResponse(response.data));
  }

  getCompanyProtocolsByResponsible(
    id,
    responsibleId,
    page = 1,
    pageSize = 5,
    search = "",
    parent = "",
    parent_info = ""
  ) {
    return this.get(
      `companies/${id}/protocols/?responsible__id=${responsibleId}&page=${page}&page_size=${pageSize}&search=${search}&parent=${parent}&parent_info=${parent_info}`
    ).then((response) => PaginatedProtocolsResponse(response.data));
  }

  getCompanyProtocolsByReporter(
    id,
    reporterId,
    page = 1,
    pageSize = 5,
    search = "",
    parent = "",
    parent_info = ""
  ) {
    return this.get(
      `companies/${id}/protocols/?reporter__id=${reporterId}&page=${page}&page_size=${pageSize}&search=${search}&parent=${parent}&parent_info=${parent_info}`
    ).then((response) => PaginatedProtocolsResponse(response.data));
  }

  getMyProtocolsByQuestionnaireType(
    questionnaireType,
    page = 1,
    pageSize = 5,
    field1 = "",
    search = "",
    responsibleId = "",
    category = "",
    reporterId = "",
    protocolStatus = "",
    parent = "",
    parent_info = "",
    survey = ""
  ) {
    let unassigned = responsibleId == "unassigned" ? 1 : "";
    let unassigned_parent = parent == "unassigned" ? 1 : "";

    if (
      store.state.auth.selectedCompanyId2 &&
      store.state.auth.selectedCompanyId2 != store.state.auth.user.company.id
    ) {
      return this.get(
        `companies/${store.state.auth.selectedCompanyId2}/protocols/?questionnaire__questionnaire_type=${questionnaireType}&page=${page}&page_size=${pageSize}&search=${search}&responsible__id=${responsibleId}&unassigned=${unassigned}&category=${category}&reporter__id=${reporterId}&status=${protocolStatus}&parent=${parent}&unassigned_parent=${unassigned_parent}&parent_info=${parent_info}&survey=${survey}`
      ).then((response) => PaginatedProtocolsResponse(response.data));
    } else {
      return this.get(
        `my/protocols/${questionnaireType}/?page=${page}&page_size=${pageSize}&field1=${field1}&search=${search}&responsible__id=${responsibleId}&unassigned=${unassigned}&category=${category}&reporter__id=${reporterId}&status=${protocolStatus}&parent=${parent}&unassigned_parent=${unassigned_parent}&parent_info=${parent_info}&survey=${survey}`
      ).then((response) => PaginatedProtocolsResponse(response.data));
    }
  }

  getMyProtocols(field1 = "", page = 1, pageSize = 5, ids = "", search = "", orderBy = null, desc = false) {
    let column = orderBy ? this.getOrderedColumnName(orderBy, desc) : null;
    let ordering = column ? `&ordering=${column}` : "";
    return this.get(`my/protocols/0/?id=${ids}&page=${page}&page_size=${pageSize}&search=${search}${ordering}`).then((response) =>
      PaginatedProtocolsResponse(response.data)
    );
  }

  getProtocolBadgeVariant(status) {
    if (status < 2) {
      return "warning";
    } else if (status < 7) {
      return "secondary";
    } else if (status == 7) {
      return "success";
    } else {
      return "light";
    }
  }

  getProtocolBadgeVariant2(status) {
    if (status < 1) {
      return "dark";
    } else if (status < 2) {
      return "warning";
    } else if (status < 7) {
      return "secondary";
    } else if (status == 7) {
      return "success";
    } else {
      return "light";
    }
  }

  getExternalProtocol(v, token, id) {
    return this.get(`external/protocol/${v}/${token}/${id}/`)
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  createExternalProtocol(v, token, id, data) {
    store.dispatch("app/setPendingChanges", false);
    return this.post(`external/protocol/${v}/${token}/${id}/`, ProtocolRequest(data))
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

  updateExternalProtocol(v, token, id, data) {
    store.dispatch("app/setPendingChanges", false);
    return this.patch(`external/protocol/${v}/${token}/${id}/`, ProtocolRequest(data))
      .then((response) => response.data)
      .then((data) => ProtocolResponse(data));
  }

}

export default new ProtocolService();
